import { useCallback, useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'ps.installDismissed'

/**
 * Drives the «افزودن» banner on the home screen. The browser only fires
 * beforeinstallprompt once a manifest and service worker are registered, so before P5
 * lands this simply never offers — which is the correct behaviour, not a stub.
 */
export function useInstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    function capture(raw: Event) {
      // Chrome shows its own mini-infobar otherwise; the design has its own banner.
      raw.preventDefault()
      setEvent(raw as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', capture)
    window.addEventListener('appinstalled', () => setEvent(null))

    return () => window.removeEventListener('beforeinstallprompt', capture)
  }, [])

  const install = useCallback(async () => {
    if (event === null) {
      return
    }

    await event.prompt()
    await event.userChoice
    setEvent(null)
  }, [event])

  const dismiss = useCallback(() => {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      // The banner will simply reappear next launch.
    }
  }, [])

  return { canInstall: event !== null && !dismissed, install, dismiss }
}
